/* 同一ページに複数セクションがあるとこのファイルは複数回実行されるため、
   トップレベル宣言が二重定義にならないよう即時関数で包む */
(function () {
  const GALLERY_AXIS_THRESHOLD = 8;
  const GALLERY_SWIPE_RATIO = 0.15;
  const GALLERY_SWIPE_MAX = 60;
  const GALLERY_EDGE_RESISTANCE = 0.3;

  class NSpecialCollectionGallery extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerEnd = this.onPointerEnd.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.index = 0;
      this.pointerId = null;
      this.startX = 0;
      this.startY = 0;
      this.deltaX = 0;
      this.isDragging = false;
      this.hasMoved = false;
    }

    connectedCallback() {
      this.track = this.querySelector('.n-special-collection-gallery__track');
      this.slides = this.querySelectorAll('.n-special-collection-gallery__slide');
      if (!this.track || this.slides.length < 2) return;

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('scroll', this.onScroll);
      /* 要素外で指やボタンを離した場合も確実に受け取るため window で拾う */
      window.addEventListener('pointerup', this.onPointerEnd);
      window.addEventListener('pointercancel', this.onPointerEnd);
      window.addEventListener('resize', this.onResize);

      this.updateActiveSlide();
    }

    disconnectedCallback() {
      window.removeEventListener('pointerup', this.onPointerEnd);
      window.removeEventListener('pointercancel', this.onPointerEnd);
      window.removeEventListener('resize', this.onResize);
    }

    /* clientWidth は整数に丸められ、枚数分ずれが累積するため実測値を使う */
    get slideWidth() {
      return this.getBoundingClientRect().width;
    }

    /* 矢印クリックもスワイプも、この 1 メソッド経由で同じトランジションで動かす */
    goTo(index, animate) {
      this.index = Math.min(Math.max(index, 0), this.slides.length - 1);
      this.translate(-this.index * this.slideWidth, animate);
      this.updateActiveSlide();
    }

    translate(x, animate) {
      this.track.classList.toggle('-is-no-transition', !animate);
      this.track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    /* 表示していないスライドの矢印にフォーカスが移ると、overflow: hidden の
       コンテナがスクロールして表示位置がずれるため、操作対象から外しておく */
    updateActiveSlide() {
      const currentIndex = this.index;
      this.slides.forEach(function (slide, index) {
        slide.inert = index !== currentIndex;
      });
    }

    onScroll() {
      if (this.scrollLeft !== 0) this.scrollLeft = 0;
    }

    onClick(event) {
      /* ドラッグ直後に発火するクリックは無視する（キーボード操作は detail が 0） */
      if (this.hasMoved) {
        this.hasMoved = false;
        if (event.detail > 0) return;
      }

      const arrow = event.target.closest('[data-gallery-direction]');
      if (!arrow) return;

      this.goTo(arrow.dataset.galleryDirection === 'next' ? this.index + 1 : this.index - 1, true);
    }

    onPointerDown(event) {
      /* 2 本目の指で状態を奪われないようにする */
      if (this.pointerId !== null) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.deltaX = 0;
      this.isDragging = false;
      this.hasMoved = false;
    }

    onPointerMove(event) {
      if (this.pointerId !== event.pointerId) return;

      /* 要素外でボタンが離されて pointerup を取り逃していた場合の保険 */
      if (event.pointerType === 'mouse' && event.buttons === 0) {
        this.onPointerEnd(event);
        return;
      }

      const distanceX = event.clientX - this.startX;
      const distanceY = event.clientY - this.startY;

      if (!this.isDragging) {
        /* 縦方向の動きが優勢なうちはページスクロールを妨げない */
        if (Math.abs(distanceX) < GALLERY_AXIS_THRESHOLD || Math.abs(distanceX) <= Math.abs(distanceY)) return;
        this.isDragging = true;
        this.hasMoved = true;
        this.setPointerCapture(event.pointerId);
      }

      this.deltaX = this.resist(distanceX);
      this.translate(-this.index * this.slideWidth + this.deltaX, false);
    }

    onPointerEnd(event) {
      if (this.pointerId !== event.pointerId) return;

      const wasDragging = this.isDragging;
      const deltaX = this.deltaX;
      this.pointerId = null;
      this.isDragging = false;
      if (!wasDragging) return;

      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

      const threshold = Math.min(this.slideWidth * GALLERY_SWIPE_RATIO, GALLERY_SWIPE_MAX);
      let index = this.index;
      if (deltaX <= -threshold) index += 1;
      if (deltaX >= threshold) index -= 1;

      this.goTo(index, true);
    }

    onResize() {
      if (this.isDragging) return;
      this.translate(-this.index * this.slideWidth, false);
    }

    /* 端でのドラッグは引っ張り量を抑えて、これ以上進めないことを伝える */
    resist(distanceX) {
      const atStart = this.index === 0 && distanceX > 0;
      const atEnd = this.index === this.slides.length - 1 && distanceX < 0;
      return atStart || atEnd ? distanceX * GALLERY_EDGE_RESISTANCE : distanceX;
    }
  }

  if (!customElements.get('n-special-collection-gallery')) {
    customElements.define('n-special-collection-gallery', NSpecialCollectionGallery);
  }
})();
